import { Link } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

export function GeographyPage() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link
          to="/ignus"
          className="text-text-muted hover:text-accent-gold transition-colors inline-flex items-center gap-1"
        >
          <BackIcon className="w-4 h-4" />
          Back to Ignus
        </Link>
      </div>

      <h1 className="font-display text-3xl font-bold text-text-primary mb-6">
        Geography of Ignus
      </h1>

      <div className="space-y-6">
        {/* World Map */}
        <div className="card p-6 border-orange-500/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
              <MapIcon className="w-5 h-5 text-orange-500" />
            </div>
            <h2 className="font-display text-xl font-semibold text-text-primary">
              The World of Ignus
            </h2>
          </div>
          <p className="text-text-secondary mb-4">
            At first glance, Ignus doesn't seem all that different from the Earth that you know. The
            continents are in different places, of course, but it's a world of mountains, plains,
            oceans, rivers, lakes, and deserts just like Earth.
          </p>
          <div className="rounded-lg overflow-hidden border border-border-default relative">
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={4}
              centerOnInit
              wheel={{ step: 0.1 }}
              pinch={{ step: 5 }}
              doubleClick={{ mode: 'reset' }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
                    <button
                      onClick={() => zoomIn()}
                      className="w-8 h-8 bg-background-card/90 hover:bg-background-elevated border border-border-default rounded flex items-center justify-center text-text-primary transition-colors"
                      aria-label="Zoom in"
                    >
                      <ZoomInIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => zoomOut()}
                      className="w-8 h-8 bg-background-card/90 hover:bg-background-elevated border border-border-default rounded flex items-center justify-center text-text-primary transition-colors"
                      aria-label="Zoom out"
                    >
                      <ZoomOutIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => resetTransform()}
                      className="w-8 h-8 bg-background-card/90 hover:bg-background-elevated border border-border-default rounded flex items-center justify-center text-text-primary transition-colors"
                      aria-label="Reset zoom"
                    >
                      <ResetIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <TransformComponent wrapperClass="!w-full" contentClass="!w-full">
                    <img
                      src="/images/ignus-world-map.jpg"
                      alt="World Map of Ignus"
                      className="w-full h-auto cursor-grab active:cursor-grabbing"
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
          <p className="text-sm text-text-muted mt-3 text-center italic">
            The world of Ignus, showing the major continents and regions
          </p>
        </div>

        {/* Regions Overview */}
        <div className="card p-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            The Regions of Ignus
          </h2>
          <p className="text-text-secondary mb-6">
            The geography of Ignus is intimately connected to its peoples. The four elemental
            peoples—the Ignan—each call different terrains home, while the scattered remnants of the
            old civilizations have built new communities across the land.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <RegionCard
              name="The Mountain Reaches"
              icon={<MountainIcon className="w-5 h-5" />}
              color="text-blue-400"
              description="Home to the People of the Peaks, the hardy Peakies who dwell in the highest places. These shaggy-furred folk are immune to cold and resistant to the crushing force of avalanches. Mountain communities dot the trade routes between lowland cities."
            />
            <RegionCard
              name="The Deep Places"
              icon={<CaveIcon className="w-5 h-5" />}
              color="text-amber-600"
              description="The underground realms where the People of the Deep make their home. These 'Dusties' navigate the darkness with darkvision and tremorsense, their plated skin protecting them from the hazards below. Ancient dwarven settlements and new mining towns share these depths."
            />
            <RegionCard
              name="The Wild Lands"
              icon={<TreeIcon className="w-5 h-5" />}
              color="text-green-400"
              description="Vast forests and untamed wilderness where the People of the Wild thrive. The most populous of the Ignan, these 'Wilders' speak with animals and plants, see through illusions, and serve as the guardians of natural balance under the Circle's guidance."
            />
            <RegionCard
              name="The Storm Coasts"
              icon={<LightningIcon className="w-5 h-5" />}
              color="text-purple-400"
              description="Coastal regions and high plateaus where storms are common, home to the People of the Tempest. These 'Stormies' channel lightning and wind, immune to falling damage as they leap from impossible heights."
            />
          </div>
        </div>

        {/* Notable Locations */}
        <div className="card p-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            Notable Locations
          </h2>

          <div className="space-y-4">
            <LocationCard
              name="Suggbury"
              type="Town"
              description="A moderately-sized town situated on a trade route between a major port city and a series of mountain communities. Suggbury is a crossroads where travelers from all walks of life pass through, making it an ideal starting point for adventurers."
            />
            <LocationCard
              name="The Bartom Groves"
              type="Sacred Sites"
              description="Scattered across Ignus are the ancient Bartom trees—massive, intelligent organisms whose root systems stretch for many miles underground. Druids trained in Bartom Keeping can sense these sacred trees within a 50-mile radius and communicate with them. The groves are protected sanctuaries of the Circle."
            />
            <LocationCard
              name="The Ruins of Raathan"
              type="Ancient Ruins"
              description="Remnants of the once-great Raathan Archive, one of the three arcanocracies destroyed in The Destruction. These ruins are dangerous places, still holding echoes of the arcane power that nearly destroyed the world. The Circle monitors them closely."
            />
            <LocationCard
              name="Temples of the Crystal Assembly"
              type="Religious Centers"
              description="The churches and temples of The Seat are found in every major settlement. These range from simple shrines to grand cathedrals where the faithful commune with the Titans of the Crystal Assembly."
            />
          </div>
        </div>

        {/* The Skyborn Territories */}
        <div className="card p-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            The Skyborn Territories
          </h2>
          <p className="text-text-secondary mb-4">
            The avian Skyborn people claim territories across Ignus, each clan preferring different
            environments suited to their nature:
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="bg-background-elevated rounded-lg p-3">
              <h4 className="font-medium text-text-primary mb-1">Clan Corva (Ravens)</h4>
              <p className="text-sm text-text-secondary">
                Urban centers and crossroads where their dangerous cleverness serves them well in
                politics and trade.
              </p>
            </div>
            <div className="bg-background-elevated rounded-lg p-3">
              <h4 className="font-medium text-text-primary mb-1">Clan Striga (Owls)</h4>
              <p className="text-sm text-text-secondary">
                Deep forests and shadowed places where they serve as nocturnal spies and watchers.
              </p>
            </div>
            <div className="bg-background-elevated rounded-lg p-3">
              <h4 className="font-medium text-text-primary mb-1">Clan Rapta (Raptors)</h4>
              <p className="text-sm text-text-secondary">
                High cliffs and open plains where their incredible speed and aerial prowess make them
                fierce warriors.
              </p>
            </div>
            <div className="bg-background-elevated rounded-lg p-3">
              <h4 className="font-medium text-text-primary mb-1">Clan Catha (Vultures)</h4>
              <p className="text-sm text-text-secondary">
                Borderlands and places others avoid. The largest Skyborn (20+ ft wingspan), they are
                immune to poison and disease.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="card p-6 bg-background-elevated">
          <h2 className="font-display text-lg font-semibold text-text-primary mb-4">
            Continue Exploring
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/ignus/intro"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <BookIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Introduction to Ignus</span>
            </Link>
            <Link
              to="/ignus/races"
              className="flex items-center gap-3 p-3 rounded-lg bg-background-card hover:bg-background-secondary transition-colors"
            >
              <SpeciesIcon className="w-5 h-5 text-accent-gold" />
              <span className="text-text-primary">Species & Lineages</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegionCard({
  name,
  icon,
  color,
  description,
}: {
  name: string;
  icon: React.ReactNode;
  color: string;
  description: string;
}) {
  return (
    <div className="bg-background-elevated rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className={color}>{icon}</span>
        <h3 className={`font-semibold ${color}`}>{name}</h3>
      </div>
      <p className="text-sm text-text-secondary">{description}</p>
    </div>
  );
}

function LocationCard({
  name,
  type,
  description,
}: {
  name: string;
  type: string;
  description: string;
}) {
  return (
    <div className="border-l-2 border-accent-gold pl-4">
      <div className="flex items-center gap-2 mb-1">
        <h4 className="font-medium text-text-primary">{name}</h4>
        <span className="text-xs px-2 py-0.5 rounded bg-accent-gold/10 text-accent-gold">
          {type}
        </span>
      </div>
      <p className="text-sm text-text-secondary">{description}</p>
    </div>
  );
}

// Icons
function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function MapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );
}

function MountainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 21l9-9 3 3 6-6M3 21h18" />
    </svg>
  );
}

function CaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function TreeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l-8 9h5v9h6v-9h5l-8-9z" />
    </svg>
  );
}

function LightningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function SpeciesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function ZoomInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
    </svg>
  );
}

function ZoomOutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM7 10h6" />
    </svg>
  );
}

function ResetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}
